/**
 * Roadmap notification tap handler.
 *
 * Web port of the six roadmap cases of Flutter's
 * `PushNavigationService.onPushNotificationAppUpdates` switch
 * (`lib/services/push_navigation_service.dart:243-300`). Every one of them makes
 * the same call — `NavigationService.navigateToRoadmapScreen(itemInfo:
 * notification.info.roadmapItemInfo, commentInfo:
 * notification.info.roadmapCommentInfo, …flags)` — and differs only in the
 * auto-open flags, so they collapse into this one handler plus an options object.
 *
 * Case table (the Task 13 router must call it with exactly these options):
 *  - `roadmapLike` (`:243-252`)          → `{ openLikes: true, likedType: true }`
 *  - `roadmapDislike` (`:253-262`)       → `{ openLikes: true, likedType: false }`
 *  - `roadmapComment` (`:263-271`)       → `{ openComments: true }`
 *  - `roadmapCommentLike` (`:272-280`)   → `{ openComments: true }`
 *  - `roadmapCommentReply` (`:281-289`)  → `{ openComments: true }`
 *  - `roadmapDonation` (`:290-300`)      → `{}` (no flags)
 *
 * `roadmapDonation` — the verified-dead `openDonations` flag. Flutter passes
 * `openDonations: true` (`:296`), but `RoadmapScreen.openDonations` is only ever
 * declared and plumbed, never read: `roadmap_screen.dart:38` / `:47`,
 * `navigation_service.dart:2632` / `:2646`, `push_navigation_service.dart:296`,
 * `from_platform_screen.dart:35` are the complete set of occurrences in `lib/`.
 * So on Flutter this tap just opens the board at that item with no sheet, and web
 * matches with a plain navigate carrying no flag. A roadmap donations sheet stays
 * deliberately deferred and is NOT a parity prerequisite (task-prompt line 19) —
 * do not introduce one, and do not add an `openDonations` flag to mirror it.
 *
 * Destination-owned behaviour — this handler only supplies flags. The
 * comments-over-likes precedence and the likes-vs-dislikes count selection live in
 * the destination (`roadmap_screen.dart:457-471`, already ported at
 * `useRoadmap.ts:218-236` + `RoadmapPage.tsx:252-282`). Do not re-implement either
 * here.
 *
 * Deliberate web adaptation — awaited-vs-not. Flutter `await`s all six navigations
 * (`:245`, `:255`, `:265`, `:274`, `:283`, `:293`); a Dart route future completes
 * when the pushed route is *popped*, deferring the caller's mark-read until the
 * user leaves the board. Web `Navigator.*` returns `void` and has no pop-future, so
 * the caller's mark-read (Task 14) fires immediately. Same end state; structural,
 * not a behaviour change.
 *
 * Pending caller: `notificationTap/notificationTapRouter.ts` (Task 13) is the first
 * and only consumer — this module is intentionally uncalled for exactly one task,
 * not dead code.
 */
import { Navigator } from '@/presentation/routing/Navigator';
import type { NotificationTapContext } from '@/presentation/feature/inbox/notificationTap/notificationTapContext';

/**
 * Per-notification-type auto-open flags for the roadmap deep link. On web these
 * ride inside the `RoadmapItemInfoEntity` payload rather than as separate widget
 * params (see that entity's header); this options object is how the router states
 * them per case.
 */
export interface RoadmapNotificationTapOptions {
  /** Auto-open the likes/dislikes sheet on the deep-linked item (`RoadmapScreen.openLikes`). */
  openLikes?: boolean;
  /** Auto-open the comments sheet on the deep-linked item (`RoadmapScreen.openComments`). */
  openComments?: boolean;
  /** Only meaningful when `openLikes` is true: true → likes, false → dislikes. */
  likedType?: boolean;
}

/**
 * Opens the roadmap board for a roadmap notification
 * (`push_navigation_service.dart:243-300`).
 *
 * A missing `roadmapItemInfo` still navigates. Flutter passes a **nullable**
 * `itemInfo` straight through (`:246`, `roadmap_screen.dart:33`) and `_getData`
 * falls back to the paginated board when it is null
 * (`roadmap_screen.dart:418-422`) — it does not no-op. `navigateToRoadmap(undefined)`
 * reproduces exactly that: no single-item mode, no auto-open. This deliberately
 * differs from the defensive guards in the other handler modules, which stand in for
 * Flutter `!` null-asserts that would *crash*; here Flutter has a defined,
 * non-crashing behaviour, so the port matches it.
 */
export const handleRoadmapNotificationTap = (
    context: NotificationTapContext,
    options: RoadmapNotificationTapOptions,
): void => {
  const itemInfo = context.notification.info.roadmapItemInfo;

  Navigator.navigateToRoadmap(
    itemInfo
      ? {
          // Spread first so any future field on `RoadmapItemInfoEntity` survives;
          // the three flags are then set explicitly — including `undefined` for
          // the flagless `roadmapDonation` shape.
          ...itemInfo,
          openLikes: options.openLikes,
          openComments: options.openComments,
          likedType: options.likedType,
        }
      : undefined,
    { roadmapCommentInfo: context.notification.info.roadmapCommentInfo },
  );
};
